<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useHashtagCompletion } from '@/composables/useHashtagCompletion'
import { useTags } from '@/composables/useTags'
import { useTodosStore } from '@/stores/todos'
import {
  deleteEmptyHashtag,
  logicText,
  normalize,
  pickTag,
  plainText,
  replaceRange,
  revertChip,
  type Segment,
} from '@/todos/chipText'
import { isTagOnly } from '@/todos/hashtags'
import { chipStyle, tagNames } from '@/todos/tags'
import HashtagSuggestions from './HashtagSuggestions.vue'

const CHIP_ATTRIBUTE = 'data-chip'

const props = defineProps<{
  placeholder?: string
  disabled?: boolean
  autofocus?: boolean
  prefillTag?: string | null // (on a tag page: new todos start with this tag as a chip)
  multiline?: boolean // (Enter is a new line and only takes a highlighted suggestion, Cmd/Ctrl + Enter submits; else Enter submits)
  maxLength?: number // (in characters of the plain text; none if not given)
}>()
const { t } = useI18n()
const emit = defineEmits<{ 'update:plain': [plain: string]; submit: [] }>()
const store = useTodosStore()
const { tags, colorFor } = useTags()

// The editor of the add fields (`QuickAdd.vue`: one line, `FloatingAdd.vue`: `multiline`) is a `contenteditable` host holding text and tag chips. The DOM is the truth while typing (it is
// parsed into `segments` on every input); picking a tag, tapping a chip, the Backspace rule and clearing after an add
// render the host again from the model (`todos/chipText.ts`). Everything else about the hashtag completion works on the
// "logic text", where a chip counts as one space, so its indexes are the caret positions of the host.
const editor = ref<HTMLDivElement | null>(null)
const segments = ref<Segment[]>([])
const text = computed(() => logicText(segments.value))
const plain = computed(() => plainText(segments.value))
const caret = ref(0)
const focused = ref(false)
const composing = ref(false)

// `plaintext-only` keeps pasted formatting out, where the browser has it; plain `true` otherwise.
function editableMode(): 'plaintext-only' | 'true' {
  const probe = document.createElement('div')
  probe.contentEditable = 'plaintext-only'
  return probe.contentEditable === 'plaintext-only' ? 'plaintext-only' : 'true'
}
const editable = editableMode()

// The hashtag completion (the list, its keyboard rules, the too-long warning) is `useHashtagCompletion`.
const names = computed(() => tagNames(store.knownTags, tags.value))
const completion = useHashtagCompletion({
  text,
  caret,
  focused,
  disabled: () => !!props.disabled,
  names: () => names.value,
  known: () => store.knownTags,
})
const { hashtag, matches, active, open, tooLongTag } = completion

// ---- the DOM <-> the model -------------------------------------------------------------------------------------------

function isChipElement(node: Node): node is HTMLElement {
  return node instanceof HTMLElement && node.hasAttribute(CHIP_ATTRIBUTE)
}

// What a piece of the host's content is worth in logic text: its characters, and one for every chip.
function logicLength(root: Node): number {
  let length = 0
  root.childNodes.forEach((child) => {
    if (child.nodeType === Node.TEXT_NODE) length += (child.textContent ?? '').length
    else if (isChipElement(child)) length += 1
    else if (child instanceof HTMLElement && child.tagName !== 'BR') length += logicLength(child)
  })
  return length
}

// The text of a text node: no non-breaking spaces; a line break is a space in the single-line field.
function textOf(node: Node): string {
  const value = (node.textContent ?? '').replace(/\u00a0/g, ' ').replace(/\r\n?/g, '\n')
  return props.multiline ? value : value.replace(/\n/g, ' ')
}

function parseEditor(): Segment[] {
  const found: Segment[] = []
  const walk = (node: Node): void => {
    node.childNodes.forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        found.push({ text: textOf(child) })
      } else if (isChipElement(child)) {
        const name = child.getAttribute(CHIP_ATTRIBUTE) ?? ''
        if (name) found.push({ chip: name })
      } else if (child instanceof HTMLElement && child.tagName !== 'BR') walk(child)
    })
  }
  if (editor.value) walk(editor.value)
  return normalize(found)
}

function chipElement(name: string): HTMLElement {
  const element = document.createElement('span')
  element.className = 'ui-chip tag'
  element.contentEditable = 'false'
  element.setAttribute(CHIP_ATTRIBUTE, name)
  element.textContent = `🏷️ ${name}`
  Object.assign(element.style, chipStyle(colorFor(name)) ?? {})
  return element
}

// (a text ending in a line break shows no empty last line unless something follows it)
function trailingBreak(): HTMLElement | null {
  const last = segments.value[segments.value.length - 1]
  if (!props.multiline || !last || 'chip' in last || !last.text.endsWith('\n')) return null
  const element = document.createElement('br')
  element.setAttribute('data-trailing', '')
  return element
}

function render(): void {
  editor.value?.replaceChildren(
    ...segments.value.map((segment) =>
      'chip' in segment ? chipElement(segment.chip) : document.createTextNode(segment.text),
    ),
    ...[trailingBreak()].filter((element) => element !== null),
  )
}

// The DOM boundary point for a logic index.
function pointAt(index: number): [Node, number] {
  const host = editor.value!
  let remaining = index
  const find = (node: Node): [Node, number] | null => {
    const children = Array.from(node.childNodes)
    for (const [position, child] of children.entries()) {
      if (child.nodeType === Node.TEXT_NODE) {
        const length = (child.textContent ?? '').length
        if (remaining <= length) return [child, remaining]
        remaining -= length
      } else if (isChipElement(child)) {
        if (remaining === 0) return [node, position]
        remaining -= 1
      } else if (child instanceof HTMLElement && child.tagName !== 'BR') {
        const inner = find(child)
        if (inner) return inner
      }
    }
    return remaining === 0 ? [node, children.length] : null
  }
  return find(host) ?? [host, host.childNodes.length]
}

function setSelection(from: number, to: number = from): void {
  const host = editor.value
  const selection = window.getSelection()
  if (!host || !selection) return
  host.focus()
  const range = document.createRange()
  range.setStart(...pointAt(from))
  range.setEnd(...pointAt(to))
  selection.removeAllRanges()
  selection.addRange(range)
  caret.value = to
}

function selectionRange(): [number, number] | null {
  const host = editor.value
  const selection = window.getSelection()
  if (!host || !selection || !selection.rangeCount || !host.contains(selection.anchorNode))
    return null
  const index = (node: Node | null, offset: number): number => {
    const range = document.createRange()
    range.selectNodeContents(host)
    range.setEnd(node!, offset)
    return logicLength(range.cloneContents())
  }
  const anchor = index(selection.anchorNode, selection.anchorOffset)
  const focus = index(selection.focusNode, selection.focusOffset)
  return [Math.min(anchor, focus), Math.max(anchor, focus)]
}

function syncCaret(): void {
  const range = selectionRange()
  if (range) caret.value = range[1]
}

function setContent(next: Segment[], position: number, selectTo: number = position): void {
  segments.value = next
  render()
  setSelection(position, selectTo)
}

// ---- events -------------------------------------------------------------------------------------------------------------

// Backstop for browsers whose `beforeinput` did not stop the overflow (Firefox for a bulk insert): the end is cut off.
function clampLength(found: Segment[]): Segment[] {
  if (props.maxLength === undefined) return found
  let excess = plainText(found).length - props.maxLength
  const kept = [...found]
  while (excess > 0 && kept.length) {
    const last = kept[kept.length - 1]
    const size = 'chip' in last ? plainText([last]).length : last.text.length
    if ('chip' in last || size <= excess) {
      kept.pop()
      excess -= size
    } else {
      kept[kept.length - 1] = { text: last.text.slice(0, size - excess) }
      excess = 0
    }
  }
  return normalize(kept)
}

function onInput(): void {
  const parsed = parseEditor()
  const clamped = composing.value ? parsed : clampLength(parsed)
  segments.value = clamped
  if (clamped !== parsed && plainText(clamped).length !== plainText(parsed).length) {
    setContent(clamped, logicText(clamped).length)
  }
  if (!composing.value) {
    if (!segments.value.length && editor.value?.childNodes.length) editor.value.replaceChildren()
    syncCaret()
    completion.typed()
  }
}

function onKeyup(event: KeyboardEvent): void {
  if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) syncCaret()
}

function pick(name: string): void {
  if (!hashtag.value) return
  const next = pickTag(segments.value, hashtag.value, name)
  completion.reset()
  setContent(next.segments, next.caret)
}

// Enter takes the highlighted suggestion; without one it is a new line in a multi-line field, else it submits.
// Cmd or Ctrl + Enter always submits, also in a multi-line field.
function onEnter(event: Event): void {
  event.preventDefault() // (the browser's own line break would be a div or br, not a plain newline)
  if (event instanceof KeyboardEvent && (event.metaKey || event.ctrlKey)) {
    emit('submit')
    return
  }
  const name = completion.accept()
  if (name !== null) pick(name)
  else if (props.multiline) insertText('\n')
  else emit('submit')
}

function onTab(event: KeyboardEvent): void {
  const name = completion.onTab(event)
  if (name !== null) pick(name)
}

function insertText(value: string): void {
  const range = selectionRange()
  if (!range) return
  const clean = props.multiline
    ? value.replace(/\r\n?/g, '\n')
    : value.replace(/\s*[\r\n]+\s*/g, ' ')
  const room =
    props.maxLength === undefined
      ? Infinity
      : props.maxLength - (plain.value.length - (range[1] - range[0]))
  const part = clean.slice(0, Math.max(0, room))
  if (!part) return
  completion.typed()
  setContent(
    replaceRange(segments.value, range[0], range[1], [{ text: part }]),
    range[0] + part.length,
  )
}

function onBeforeInput(event: InputEvent): void {
  switch (event.inputType) {
    case 'insertParagraph':
    case 'insertLineBreak':
      // (virtual keyboards send no usable Enter key, only this)
      onEnter(event)
      return
    case 'insertFromPaste':
    case 'insertFromDrop':
    case 'insertFromYank': {
      event.preventDefault()
      const data = event.dataTransfer?.getData('text/plain') ?? event.data ?? ''
      insertText(data)
      return
    }
    case 'deleteContentBackward': {
      const range = selectionRange()
      if (!range || range[0] !== range[1]) return
      const result = deleteEmptyHashtag(segments.value, range[0])
      if (!result) return
      event.preventDefault() // (not the browser's: it would only take the `#` or the opening quote)
      completion.close()
      setContent(result.segments, result.caret)
      return
    }
    case 'insertText':
    case 'insertReplacementText': {
      // (the length limit of the old input: what does not fit is cut off)
      const range = selectionRange()
      const data = event.data ?? ''
      if (props.multiline && /[\r\n]/.test(data)) {
        // (a bulk insert with line breaks, e.g. from a keyboard's clipboard suggestion: the browser would make a div or br)
        event.preventDefault()
        insertText(data)
        return
      }
      if (
        range &&
        props.maxLength !== undefined &&
        plain.value.length - (range[1] - range[0]) + data.length > props.maxLength
      ) {
        event.preventDefault()
        insertText(data)
      }
      return
    }
  }
}

function onClick(event: MouseEvent): void {
  const target = event.target
  const chip = target instanceof Element ? target.closest(`[${CHIP_ATTRIBUTE}]`) : null
  if (!chip || !editor.value || !editor.value.contains(chip)) {
    syncCaret()
    return
  }
  // a tapped chip becomes its hashtag again, with the name selected
  const range = document.createRange()
  range.selectNodeContents(editor.value)
  range.setEndBefore(chip)
  const reverted = revertChip(segments.value, logicLength(range.cloneContents()))
  if (!reverted) return
  completion.reset()
  setContent(reverted.segments, reverted.selection[0], reverted.selection[1])
}

function onSelectionChange(): void {
  if (focused.value) syncCaret()
}

// The field's content when it is empty: the tag of the tag page as a chip, a space behind it.
function prefillSegments(tag: string | null | undefined): Segment[] {
  return tag ? [{ chip: tag }, { text: ' ' }] : []
}

// A tag page for another tag (the page component is reused): the prefill follows, typed text stays.
watch(
  () => props.prefillTag,
  (next, previous) => {
    if (previous && !isTagOnly(plain.value, previous)) return
    segments.value = prefillSegments(next)
    render()
    caret.value = logicText(segments.value).length
  },
)

// Back to the empty field (the prefilled tag, if there is one), e.g. after an add.
function clear(): void {
  completion.reset()
  const next = prefillSegments(props.prefillTag)
  setContent(next, logicText(next).length)
}

watch(plain, (value) => emit('update:plain', value), { immediate: true })

onMounted(() => {
  if (props.prefillTag) {
    // (rendered only: selecting would take the focus on page load)
    segments.value = prefillSegments(props.prefillTag)
    render()
    caret.value = logicText(segments.value).length
  }
  document.addEventListener('selectionchange', onSelectionChange)
  if (props.autofocus) {
    editor.value?.focus()
    if (props.prefillTag) setSelection(logicText(segments.value).length)
  }
})
onBeforeUnmount(() => document.removeEventListener('selectionchange', onSelectionChange))

defineExpose({ focus: () => void nextTick(() => editor.value?.focus()), clear })
</script>

<template>
  <div class="field">
    <div
      ref="editor"
      class="ui-input editor"
      :class="{ multiline }"
      role="textbox"
      :aria-multiline="multiline ? 'true' : 'false'"
      aria-autocomplete="list"
      :aria-label="placeholder ?? t('todos.addPlaceholder')"
      :aria-disabled="disabled"
      :contenteditable="disabled ? 'false' : editable"
      :data-placeholder="placeholder ?? t('todos.addPlaceholder')"
      :enterkeyhint="multiline ? 'enter' : 'done'"
      spellcheck="true"
      autocapitalize="sentences"
      @focus="((focused = true), syncCaret())"
      @blur="focused = false"
      @input="onInput"
      @beforeinput="onBeforeInput"
      @compositionstart="composing = true"
      @compositionend="((composing = false), onInput())"
      @click="onClick"
      @keyup="onKeyup"
      @keydown.down="completion.move(1, $event)"
      @keydown.up="completion.move(-1, $event)"
      @keydown.enter="onEnter"
      @keydown.tab="onTab"
      @keydown.esc="completion.onEscape"
    ></div>
    <HashtagSuggestions
      :matches="matches"
      :active="active"
      :open="open"
      :too-long="tooLongTag"
      :placement="multiline ? 'inside' : 'above'"
      @pick="pick"
    />
  </div>
</template>

<style scoped>
.field {
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.editor {
  min-height: 2.35rem;
  overflow-x: auto;
  white-space: pre;
  line-height: 1.5;
  scrollbar-width: none;
}

.editor.multiline {
  flex: 1;
  min-height: 12rem;
  overflow-y: auto;
  overflow-wrap: anywhere;
  white-space: pre-wrap;
}

.editor::-webkit-scrollbar {
  display: none;
}

.editor:empty::before {
  content: attr(data-placeholder);
  color: var(--tg-hint-color);
  pointer-events: none;
}

.editor[contenteditable='false'] {
  opacity: 0.6;
}

/* (the chips are created in the script, so scoped styles need :deep) */
.editor :deep(.ui-chip) {
  margin: 0 0.1rem;
  font-size: 0.9rem;
  color: var(--tg-text-color);
  user-select: none;
  cursor: pointer;
}
</style>
